/**
 * Availability Filter - Client-side filtering for in-stock products
 * Hides/shows products based on data-product-available attribute
 */
(function() {
  'use strict';

  const AVAILABILITY_PARAM = 'availability';
  const IN_STOCK_VALUE = 'in_stock';

  // State
  let isFilterActive = false;

  /**
   * Get all product cards on the page
   */
  function getProductCards() {
    return document.querySelectorAll('[data-product-available]');
  }

  /**
   * Get all availability filter checkboxes
   */
  function getFilterCheckboxes() {
    return document.querySelectorAll('[data-availability-filter], [data-availability-filter-mobile]');
  }

  /**
   * Get availability pills
   */
  function getAvailabilityPills() {
    return document.querySelectorAll('[data-availability-pill]');
  }

  /**
   * Get reset buttons
   */
  function getResetButtons() {
    return document.querySelectorAll('[data-availability-reset], [data-availability-reset-mobile]');
  }

  /**
   * Check if filter should be active from URL
   */
  function getFilterStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get(AVAILABILITY_PARAM) === IN_STOCK_VALUE;
  }

  /**
   * Update URL with filter state
   */
  function updateURL(active) {
    const url = new URL(window.location.href);
    if (active) {
      url.searchParams.set(AVAILABILITY_PARAM, IN_STOCK_VALUE);
    } else {
      url.searchParams.delete(AVAILABILITY_PARAM);
    }
    window.history.replaceState({}, '', url.toString());
  }

  // Category filter state
  let activeCategories = new Set();

  /**
   * Apply all client-side filters to product cards
   */
  function applyFilter() {
    const cards = getProductCards();
    let visibleCount = 0;
    let totalCount = cards.length;

    cards.forEach(card => {
      const gridItem = card.closest('.grid__item');
      if (!gridItem) return;

      const isAvailable = card.getAttribute('data-product-available') === 'true';
      const productType = (card.getAttribute('data-product-type') || '').toLowerCase();

      let show = true;

      if (isFilterActive && !isAvailable) {
        show = false;
      }

      if (activeCategories.size > 0 && !activeCategories.has(productType)) {
        show = false;
      }

      gridItem.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });

    updateProductCount(visibleCount, totalCount);
    updateUIState();
  }

  /**
   * Whether the size filter (size-filter.js) is currently driving the count.
   * Its active state lives entirely in the URL hash (#size=...), so we read
   * that instead of reaching into the other module — when a size filter is
   * active we must NOT clobber the count it owns.
   */
  function isSizeFilterActive() {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const size = params.get('size');
    return !!(size && size.length);
  }

  /**
   * Update product count display.
   *
   * Caches the original server-rendered "X products" text, then restores it
   * when the availability filter is cleared — so the label doesn't stay frozen
   * at "X of Y products" after turning the filter off.
   *
   * The cache is only ever populated when NEITHER this filter NOR size-filter.js
   * is active, so the captured string is guaranteed to be the untouched
   * server-rendered count — never a size-/availability-filtered value. This
   * avoids a script-load-order race where size-filter.js could have already
   * rewritten #ProductCount before this module first runs.
   */
  let originalCountText = null;
  function captureOriginal(countElements) {
    if (originalCountText === null && countElements.length && !isFilterActive && !isSizeFilterActive()) {
      originalCountText = countElements[0].textContent;
    }
  }
  function updateProductCount(visible, total) {
    const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
    captureOriginal(countElements);

    if (!isFilterActive) {
      // Don't fight size-filter.js — it owns the count while its hash is set.
      if (originalCountText !== null && !isSizeFilterActive()) {
        countElements.forEach(el => {
          el.textContent = originalCountText;
        });
      }
      return;
    }

    countElements.forEach(el => {
      if (visible !== total) {
        el.textContent = `${visible} of ${total} products`;
      }
    });
  }

  /**
   * Update UI elements to reflect filter state
   */
  function updateUIState() {
    // Update checkboxes
    getFilterCheckboxes().forEach(checkbox => {
      checkbox.checked = isFilterActive;
    });

    // Update pills
    getAvailabilityPills().forEach(pill => {
      if (isFilterActive) {
        pill.classList.remove('hidden');
      } else {
        pill.classList.add('hidden');
      }
    });

    // Update reset buttons
    getResetButtons().forEach(btn => {
      if (isFilterActive) {
        btn.classList.remove('hidden');
      } else {
        btn.classList.add('hidden');
      }
    });

    // Update count indicator in vertical filter
    const countIndicators = document.querySelectorAll('.availability-count');
    countIndicators.forEach(indicator => {
      if (isFilterActive) {
        indicator.classList.remove('hidden');
      } else {
        indicator.classList.add('hidden');
      }
    });

    // Update selected text in horizontal filter
    const selectedTexts = document.querySelectorAll('.availability-selected-text');
    selectedTexts.forEach(text => {
      text.textContent = isFilterActive ? '1 selected' : '';
    });
  }

  /**
   * Toggle the filter state
   */
  function toggleFilter(active) {
    isFilterActive = active;
    updateURL(isFilterActive);
    applyFilter();
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    // Stop the availability checkbox's input event from bubbling to
    // <facet-filters-form>, which would otherwise submit the form and re-render
    // (and wipe out) our client-side filter. Capture phase so we beat the form's listener.
    document.addEventListener('input', function(e) {
      if (e.target.matches('[data-availability-filter], [data-availability-filter-mobile]')) {
        e.stopPropagation();
      }
    }, true);

    // Checkbox change events
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-availability-filter], [data-availability-filter-mobile]')) {
        e.stopPropagation();
        toggleFilter(e.target.checked);
      }
    }, true);

    // Pill click events
    document.addEventListener('click', function(e) {
      const pill = e.target.closest('[data-availability-pill]');
      if (pill) {
        e.preventDefault();
        toggleFilter(false);
      }

      const resetBtn = e.target.closest('[data-availability-reset], [data-availability-reset-mobile]');
      if (resetBtn) {
        e.preventDefault();
        toggleFilter(false);
      }
    });
  }

  /**
   * Set up MutationObserver to watch for new products (infinite scroll)
   */
  function setupMutationObserver() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const observer = new MutationObserver(function(mutations) {
      let hasNewProducts = false;

      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && (node.matches('.grid__item') || node.querySelector('[data-product-available]'))) {
            hasNewProducts = true;
          }
        });
      });

      if (hasNewProducts) {
        buildCategoryFilter();
        if (isFilterActive || activeCategories.size > 0) {
          applyFilter();
        }
      }
    });

    observer.observe(productGrid, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Build category filter options from product cards on the page
   */
  function buildCategoryFilter() {
    const cards = getProductCards();
    const typeCounts = {};

    cards.forEach(card => {
      const type = card.getAttribute('data-product-type');
      if (type) {
        const key = type.toLowerCase();
        typeCounts[key] = typeCounts[key] || { label: type, count: 0 };
        typeCounts[key].count++;
      }
    });

    const lists = document.querySelectorAll('[data-category-filter-list]');
    const sections = document.querySelectorAll('[data-category-filter-section]');

    // Hide section if no types found
    if (Object.keys(typeCounts).length <= 1) {
      sections.forEach(s => s.style.display = 'none');
      return;
    }

    // Sort by count descending
    const sorted = Object.entries(typeCounts).sort((a, b) => b[1].count - a[1].count);

    lists.forEach(list => {
      list.innerHTML = '';
      sorted.forEach(([key, data]) => {
        const li = document.createElement('li');
        li.className = 'filters-panel__filter-item';

        const label = document.createElement('label');
        label.className = 'filters-panel__filter-label';
        label.setAttribute('data-category-value', key);

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'filters-panel__checkbox';
        checkbox.setAttribute('data-category-checkbox', '');
        checkbox.value = key;

        const check = document.createElement('span');
        check.className = 'filters-panel__filter-check';
        check.textContent = '\u2713';

        const text = document.createElement('span');
        text.textContent = data.label;

        label.appendChild(checkbox);
        label.appendChild(check);
        label.appendChild(text);
        li.appendChild(label);
        list.appendChild(li);
      });
    });

    sections.forEach(s => s.style.display = '');
  }

  /**
   * Set up category filter event listeners
   */
  function setupCategoryListeners() {
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-category-checkbox]')) {
        const value = e.target.value;
        if (e.target.checked) {
          activeCategories.add(value);
        } else {
          activeCategories.delete(value);
        }

        // Sync all matching checkboxes
        document.querySelectorAll('[data-category-checkbox][value="' + CSS.escape(value) + '"]').forEach(cb => {
          cb.checked = e.target.checked;
        });

        // Update URL
        const url = new URL(window.location.href);
        if (activeCategories.size > 0) {
          url.searchParams.set('category', Array.from(activeCategories).join(','));
        } else {
          url.searchParams.delete('category');
        }
        window.history.replaceState({}, '', url.toString());

        applyFilter();
      }
    });
  }

  /**
   * Restore category filter state from URL
   */
  function getCategoryStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get('category');
    if (cat) {
      cat.split(',').forEach(c => activeCategories.add(c));
    }
  }

  /**
   * Initialize the availability filter
   */
  function init() {
    // Check URL for initial state
    isFilterActive = getFilterStateFromURL();
    getCategoryStateFromURL();

    // Set up event listeners
    setupEventListeners();
    setupCategoryListeners();

    // Build category filter
    buildCategoryFilter();

    // Restore category checkbox state
    if (activeCategories.size > 0) {
      activeCategories.forEach(cat => {
        document.querySelectorAll('[data-category-checkbox][value="' + CSS.escape(cat) + '"]').forEach(cb => {
          cb.checked = true;
        });
      });
    }

    // Set up mutation observer for infinite scroll
    setupMutationObserver();

    // Apply initial filter state
    if (isFilterActive || activeCategories.size > 0) {
      applyFilter();
    } else {
      updateUIState();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
